import { Divider, Grid, Typography } from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import { EJRecordTypes } from 'src/common/enumerations/ej-record-types';
import { IElectronicJournalRecord } from 'src/common/interfaces/states/IElectronicJournalRecord';
import Pagination from 'src/pagination/pagination';
import { couponStyles } from './summary-menu-styles';
import { SvgCoupon, SvgCouponActive } from 'src/item-list/components/item-list-icons';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

export const CouponSummaryListView: React.FC<CouponSummaryMenuProps> = ({
  title,
  items,
  testIdPrefix,
}): JSX.Element => {
  const ejList = items.filter(i => i.Type !== EJRecordTypes.Product);
  let couponList = ejList.filter(
    coupon =>
      !coupon.Voided &&
      (coupon.Type === EJRecordTypes.Coupon || coupon.Type === EJRecordTypes.IssuedCoupon),
  );
  const quantities = handleQuantities(couponList);
  couponList = _.uniqBy(couponList, 'Name');
  const [numPages, setNumPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = getPageSize();

  useEffect(() => {
    setNumPages(Math.ceil(couponList.length / PAGE_SIZE));
  }, [couponList, currentPage, PAGE_SIZE]);

  const handlePageChange = useCallback(
    (updatedPage: number): void => {
      if (updatedPage > currentPage) {
        setCurrentPage(currentPage + 1);
      } else {
        setCurrentPage(currentPage - 1);
      }
    },
    [currentPage],
  );

  const classes = couponStyles();
  return (
    <Grid className={classes.outermostGrid} data-testid={`${testIdPrefix}-summary-menu`}>
      <Grid className={classes.body}>
        <Grid>
          <Typography
            className={classes.summaryLabel}
            data-testid={`${testIdPrefix}-summary-menu-label`}
          >
            {title} (
            {ejList
              .filter(
                i =>
                  !i.Voided &&
                  (i.Type === EJRecordTypes.Coupon || i.Type === EJRecordTypes.IssuedCoupon),
              )
              .reduce((total, current) => {
                return total + (current?.Quantity ?? 0);
              }, 0)}
            )
          </Typography>
        </Grid>
        <Grid container direction='row' className={classes.couponListContainer}>
          <CouponItemList
            couponList={couponList}
            currentPage={currentPage}
            pageSize={PAGE_SIZE}
            quantities={quantities}
          />
        </Grid>
      </Grid>
      <Grid className={classes.footer}>
        <Grid container className={classes.couponPagination}>
          <Pagination
            currentPage={currentPage}
            totalPages={numPages}
            updateCurrentPageHandler={handlePageChange}
            testIdPrefix={'coupon-summary'}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

const CouponItemList: React.FC<CouponItemListProps> = ({
  couponList,
  currentPage,
  pageSize,
  quantities,
}): JSX.Element => {
  const classes = couponStyles();
  const couponPage: IElectronicJournalRecord[] | undefined = couponList?.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );
  const emptySpaceLength = pageSize - couponPage.length;
  handleEmptySpace(emptySpaceLength, couponPage);
  return (
    <Grid item className={classes.couponListBody}>
      {couponPage.map((coupon, couponIndex) => {
        return (
          <React.Fragment key={`couponListItem-${couponIndex}`}>
            <CouponItem coupon={coupon} id={couponIndex++} quantities={quantities} />
            <Divider
              variant='middle'
              style={coupon.Name ? {} : { backgroundColor: 'transparent' }}
            />
          </React.Fragment>
        );
      })}
    </Grid>
  );
};

const CouponItem: React.FC<CouponItemProps> = ({ coupon, id, quantities }): JSX.Element => {
  const classes = couponStyles();
  const { t } = useTranslation();
  return (
    <>
      <Grid container className={classes.couponListItem}>
        {coupon.Name && (
          <Grid container item direction='row' style={{ alignItems: 'center' }}>
            <Grid item className={classes.couponIconContainer}>
              {coupon.CouponRedemptionState?.Redeemed ? (
                <SvgCouponActive
                  width='4vh'
                  height=''
                  fill='#54AF48'
                  style={{ verticalAlign: 'middle' }}
                />
              ) : (
                <SvgCoupon width='4vh' height='' style={{ verticalAlign: 'middle' }} />
              )}
            </Grid>
            <Grid item container direction='column' className={classes.couponGroupItem}>
              <Grid item className={classes.summaryGrid} data-testid={`coupon-name-text-${id}`}>
                <Typography className={classes.summaryLabel}>{coupon.Name}</Typography>
              </Grid>
              <Grid container>
                <Grid item data-testid={`coupon-status-text-${id}`}>
                  <Typography className={classes.summaryText}>
                    {coupon.Type === EJRecordTypes.Coupon
                      ? coupon.CouponRedemptionState?.Redeemed
                        ? t('RedeemedCouponLabel')
                        : t('UnusedCouponLabel')
                      : t('IssuedCouponLabel')}
                  </Typography>
                </Grid>
                <Grid item data-testid={`coupon-quantity-text-${id}`}>
                  <Typography className={classes.summaryText}>
                    &nbsp;| QTY {quantities[coupon.Name].count ?? 0}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  );
};

const handleQuantities = (couponList: IElectronicJournalRecord[]): CouponQuantity => {
  const counts: CouponQuantity = {};
  couponList.forEach(coupon => {
    if (counts[coupon.Name] !== undefined) {
      counts[coupon.Name].count += coupon.Quantity;
    } else {
      counts[coupon.Name] = {
        count: coupon.Quantity,
      };
    }
  });
  return counts;
};

export const getPageSize = (): number => {
  const screenWidth = document.documentElement.clientWidth;
  const screenHeight = document.documentElement.clientHeight;
  if (screenWidth === 1600 && screenHeight === 1200) {
    return 10;
  }
  return 8;
};

const handleEmptySpace = (emptySpaceLength: number, arr: IElectronicJournalRecord[]): void => {
  for (let i = 0; i < emptySpaceLength; i++) {
    const emptyItem: IElectronicJournalRecord = {
      SequenceNumber: -i,
      Name: '',
      Voided: false,
      Price: '',
      Type: EJRecordTypes.Coupon,
      Selected: false,
      EligibilityInfo: [],
    };
    arr.push(emptyItem);
  }
};

interface CouponQuantity {
  quantity?: number;
}
interface CouponItemProps {
  coupon: IElectronicJournalRecord;
  id: number;
  quantities: CouponQuantity;
}
interface CouponItemListProps {
  couponList: IElectronicJournalRecord[];
  currentPage: number;
  pageSize: number;
  quantities: CouponQuantity;
}
interface CouponSummaryMenuProps {
  title: string;
  items: IElectronicJournalRecord[];
  testIdPrefix: string;
}
